import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal, { SectionLabel } from "@/components/Reveal";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { calculatePosition } from "@/lib/risk-engine.mjs";
import {
  Calculator, ChevronDown, ShieldAlert, Info, AlertTriangle,
  Sparkles, Ban,
} from "lucide-react";

const MARKETS = [
  ["XAUUSD", "Gold"],
  ["NAS100", "NASDAQ"],
  ["US30", "US30"],
  ["US500", "S&P 500"],
  ["XAGUSD", "Silver"],
  ["EURUSD", "Forex"],
  ["BTCUSD", "Bitcoin"],
  ["ETHUSD", "Ethereum"],
  ["OTHER", "Other"],
];

const RISK_OPTIONS = [0.5, 1, 2];

const SPEC_FIELDS = new Set([
  "market", "symbol", "broker", "currency", "otherCurrency", "method",
  "minLot", "volumeStep", "maxLot", "tickSize", "tickValueLoss", "probeVolume", "quoteLoss",
]);

const INITIAL_SPEC = {
  symbol: "", currency: "USD", otherCurrency: "", basis: "Equity", method: "tick",
  minLot: "", volumeStep: "", maxLot: "", tickSize: "", tickValueLoss: "",
  probeVolume: "", quoteLoss: "", commissionPerLot: "0", extraTicks: "0",
  freeMargin: "", marginPerLot: "",
};

const ILLUSTRATION_SPEC = {
  symbol: "XAUUSD-DEMO", currency: "USD", otherCurrency: "", basis: "Equity", method: "tick",
  minLot: "0.01", volumeStep: "0.01", maxLot: "100", tickSize: "0.01", tickValueLoss: "1",
  probeVolume: "", quoteLoss: "", commissionPerLot: "0", extraTicks: "0",
  freeMargin: "", marginPerLot: "",
};

const inputCls =
  "mt-1.5 w-full bg-ink/70 border border-line rounded-md px-4 py-3 text-base text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 transition-colors";
const advInputCls =
  "mt-1 w-full bg-ink/70 border border-line rounded-sm px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400";
const helpCls = "mt-1 block text-[11px] text-slate-500 leading-relaxed";

const AdvField = ({ id, label, help, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
    {help && <span className={helpCls}>{help}</span>}
  </div>
);

const PositionSizing = () => {
  const [market, setMarket] = useState("XAUUSD");
  const [broker, setBroker] = useState("");
  const [balance, setBalance] = useState("");
  const [riskPct, setRiskPct] = useState(1);
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [spec, setSpec] = useState(INITIAL_SPEC);
  const [verified, setVerified] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMargin, setShowMargin] = useState(false);

  const currencyCode = spec.currency === "OTHER" ? spec.otherCurrency.trim().toUpperCase() : spec.currency;
  const money = (v) => {
    if (/^[A-Z]{3}$/.test(currencyCode)) {
      try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: currencyCode, maximumFractionDigits: 2 }).format(v);
      } catch { /* fall through */ }
    }
    return `${Number(v).toFixed(2)} ${currencyCode || "account units"}`;
  };
  const nfmt = (v) => Number(v).toLocaleString("en-GB", { maximumFractionDigits: 6 });

  const setSpecField = (key, value) => {
    setSpec((prev) => ({ ...prev, [key]: value }));
    if (demoMode) setDemoMode(false);
    if (SPEC_FIELDS.has(key)) setVerified(false);
  };

  const loadIllustration = () => {
    setSpec(ILLUSTRATION_SPEC);
    setDemoMode(true);
    setVerified(false);
  };

  const simpleReady = Number(balance) > 0 && Number(entry) > 0 && Number(stop) > 0 && entry !== stop;

  const computed = useMemo(() => {
    if (!simpleReady) return null;
    const b = Number(balance);
    const riskBudget = b * riskPct / 100;
    const side = Number(stop) < Number(entry) ? "buy" : "sell";
    if (!verified) return { riskBudget, engine: null, engineError: null };
    try {
      const engine = calculatePosition({
        ...spec, accountValue: balance, riskPercent: String(riskPct),
        entry, stop, side, verified,
      });
      return { riskBudget, engine, engineError: null };
    } catch (e) {
      return { riskBudget, engine: null, engineError: e.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, riskPct, entry, stop, spec, verified, simpleReady]);

  const engine = computed?.engine;
  const marketLabel = MARKETS.find(([v]) => v === market)?.[1] || "this market";

  const simpleWarning = (w) => {
    if (w.startsWith("Risk is above")) return "You're risking more than 2% — most beginners stay at 1% or less.";
    if (w.startsWith("No additional adverse")) return "Prices can jump past your stop in fast markets, so the real loss can be a little bigger.";
    if (w.startsWith("Commission is set to zero")) return "Broker fees aren't included here, so your real risk may be slightly higher.";
    if (w.startsWith("Margin has not been checked")) return "We haven't checked whether your account has enough margin for this size — confirm in MT5.";
    if (w.startsWith("Margin check incomplete")) return "Margin check is half-finished — fill both margin boxes or leave both empty.";
    if (w.startsWith("Estimated required margin")) return "This size looks too big for your available margin — don't treat it as affordable.";
    return w;
  };

  return (
    <div className="noise bg-ink text-slate-200 min-h-screen" data-testid="position-sizing-page">
      <Nav />
      <main className="pt-32 pb-4">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionLabel>Atlas Academy · Tool 01</SectionLabel>
            <h1 className="mt-5 max-w-3xl font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.02]" data-testid="calc-title">
              Risk first.
              <span className="text-gold-gradient"> Lots second.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">
              Know exactly how much you're putting on the line before you enter a trade —
              in plain numbers, not trading jargon.
            </p>
          </Reveal>

          {/* ---------- Simple calculator card ---------- */}
          <Reveal delay={0.1}>
            <div className="mt-12 max-w-3xl mx-auto border border-line rounded-lg bg-card2/40 p-6 sm:p-10" data-testid="calc-form">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                    <Calculator size={18} className="text-gold" />
                  </span>
                  <h2 className="font-display font-bold text-xl text-slate-100">ATLAS Lot Size Calculator</h2>
                </div>
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase border border-line text-slate-500 px-2.5 py-1 rounded-full" data-testid="calc-mode-badge">
                  Educational tool
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-400">Know your risk before entering a trade.</p>

              <div className="mt-8 space-y-7">
                <div>
                  <p className="text-sm font-medium text-slate-200 mb-3">1. What are you trading?</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="calc-market-group" role="radiogroup" aria-label="Market">
                    {MARKETS.map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={market === v}
                        onClick={() => { setMarket(v); setVerified(false); }}
                        data-testid={`calc-market-${v.toLowerCase()}`}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-colors duration-300 ${
                          market === v
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-line text-slate-400 hover:border-gold/40 hover:text-slate-200"
                        }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${market === v ? "border-gold" : "border-slate-600"}`}>
                          {market === v && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                        </span>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="calc-broker" className="text-sm font-medium text-slate-200">2. Which broker or prop firm are you with?</label>
                  <input
                    id="calc-broker"
                    data-testid="calc-broker"
                    className={inputCls}
                    placeholder="e.g. PU Prime"
                    autoComplete="off"
                    value={broker}
                    onChange={(e) => { setBroker(e.target.value); setVerified(false); }}
                  />
                </div>

                <div>
                  <label htmlFor="calc-balance" className="text-sm font-medium text-slate-200">3. Account balance ($)</label>
                  <input
                    id="calc-balance"
                    data-testid="calc-balance"
                    type="number" min="0" step="any" inputMode="decimal"
                    className={inputCls}
                    placeholder="e.g. 1000"
                    value={balance}
                    onChange={(e) => setBalance(e.target.value)}
                  />
                </div>

                <div>
                  <p className="text-sm font-medium text-slate-200 mb-3">4. How much do you want to risk?</p>
                  <div className="flex gap-2.5" data-testid="calc-risk-group" role="radiogroup" aria-label="Risk percentage">
                    {RISK_OPTIONS.map((r) => (
                      <button
                        key={r}
                        type="button"
                        role="radio"
                        aria-checked={riskPct === r}
                        onClick={() => setRiskPct(r)}
                        data-testid={`calc-risk-${String(r).replace(".", "-")}`}
                        className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-colors duration-300 ${
                          riskPct === r
                            ? "border-gold bg-gold text-ink"
                            : "border-line text-slate-400 hover:border-gold/40 hover:text-slate-200"
                        }`}
                      >
                        {r}%
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">Most beginners start at 1%. This is the most you're willing to lose if the trade goes wrong.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="calc-entry" className="text-sm font-medium text-slate-200">5. Entry price</label>
                    <input
                      id="calc-entry"
                      data-testid="calc-entry"
                      type="number" min="0" step="any" inputMode="decimal"
                      className={inputCls}
                      placeholder="e.g. 4150"
                      value={entry}
                      onChange={(e) => setEntry(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="calc-stop" className="text-sm font-medium text-slate-200">6. Stop-loss price</label>
                    <input
                      id="calc-stop"
                      data-testid="calc-stop"
                      type="number" min="0" step="any" inputMode="decimal"
                      className={inputCls}
                      placeholder="e.g. 4145"
                      value={stop}
                      onChange={(e) => setStop(e.target.value)}
                    />
                    <p className="mt-2 text-[11px] text-slate-500">The price where you'll exit if the trade goes against you. Buy or sell — we work that out for you.</p>
                  </div>
                </div>
              </div>

              {/* ---------- Result ---------- */}
              <div className="mt-9 pt-8 border-t border-line/70" data-testid="calc-result-panel">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-6">Your Risk Plan</p>
                <div className="space-y-5">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-400">Maximum planned risk</p>
                    <p className="font-display font-bold text-2xl text-slate-50 tabular-nums" data-testid="result-budget">
                      {computed ? money(computed.riskBudget) : "—"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-400">You're risking</p>
                    <p className="font-display font-bold text-2xl text-slate-50 tabular-nums" data-testid="result-risk-percent">
                      {riskPct}%
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-400">Suggested MT5 lot size</p>
                    <p
                      className={`font-display font-extrabold text-3xl tabular-nums ${
                        engine?.noTrade ? "text-red-400" : engine ? "text-gold" : "text-slate-600"
                      }`}
                      data-testid="result-lots"
                    >
                      {engine ? (engine.noTrade ? "NO TRADE" : nfmt(engine.lots)) : "—"}
                    </p>
                  </div>
                </div>

                {/* status notes */}
                {entry && stop && entry === stop && (
                  <div className="mt-6 flex items-start gap-3 border border-amber-500/30 bg-amber-500/5 rounded-md px-4 py-3.5" data-testid="calc-same-price-note">
                    <AlertTriangle size={15} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-300 leading-relaxed">Your entry and stop-loss can't be the same price — move your stop away from your entry.</p>
                  </div>
                )}

                {!engine && !computed?.engineError && (
                  <div className="mt-6 flex items-start gap-3 border border-line rounded-md bg-ink/40 px-4 py-3.5" data-testid="calc-unverified-note">
                    <Info size={15} className="text-gold mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-400 leading-relaxed" data-testid="calc-unverified-text">
                      We can't suggest a safe lot size yet. Every broker prices {marketLabel} slightly
                      differently, and guessing could put more money at risk than you planned. The lot
                      size appears here once the correct broker and MT5 symbol specifications are
                      verified in the advanced section below.
                    </p>
                  </div>
                )}

                {computed?.engineError && (
                  <div className="mt-6 flex items-start gap-3 border border-amber-500/30 bg-amber-500/5 rounded-md px-4 py-3.5" data-testid="calc-error-note">
                    <AlertTriangle size={15} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Something in the advanced specifications needs attention: {computed.engineError}
                    </p>
                  </div>
                )}

                {engine?.noTrade && (
                  <div className="mt-6 flex items-start gap-3 border border-red-500/40 bg-red-500/10 rounded-md px-4 py-3.5" data-testid="calc-notrade-note">
                    <Ban size={15} className="text-red-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-200 leading-relaxed" data-testid="calc-notrade-text">
                      Even the smallest allowed trade would risk about <span className="text-red-300 font-semibold">{money(engine.minRisk)}</span> — more
                      than your <span className="font-semibold">{money(computed.riskBudget)}</span> plan. Skip this
                      trade, or use a wider stop-loss / bigger balance.
                    </p>
                  </div>
                )}

                {engine && !engine.noTrade && (
                  <div className="mt-6 space-y-2.5" data-testid="calc-result-notes">
                    <div className="flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/5 rounded-md px-4 py-3.5">
                      <Info size={15} className="text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-slate-300 leading-relaxed" data-testid="calc-success-text">
                        This size keeps you at about <span className="text-emerald-300 font-semibold">{money(engine.estimatedRisk)}</span> ({engine.actualPct.toFixed(2)}%)
                        if your stop is hit. It's an estimate — always double-check on your MT5 before trading.
                      </p>
                    </div>
                    {engine.warnings.map((w, wi) => (
                      <p key={w} className="flex items-start gap-2 text-xs text-amber-300/90 leading-relaxed px-1" data-testid={`calc-warning-${wi + 1}`}>
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        {simpleWarning(w)}
                      </p>
                    ))}
                    {demoMode && (
                      <p className="flex items-start gap-2 text-xs text-amber-300/90 leading-relaxed px-1" data-testid="calc-demo-note">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        You're looking at example specifications — NOT real broker data. Don't trade on this number.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ---------- Advanced ---------- */}
              <div className="mt-9 pt-6 border-t border-line/70">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  data-testid="advanced-settings-toggle"
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors font-medium"
                >
                  <ChevronDown size={14} className={`transition-transform duration-300 ${showAdvanced ? "rotate-180" : ""}`} />
                  Advanced — broker &amp; MT5 specifications (for verified setups)
                </button>

                {showAdvanced && (
                  <div className="mt-6 space-y-4" data-testid="advanced-settings-panel">
                    <div className="flex items-start gap-3 border border-line rounded-sm bg-ink/40 px-4 py-3">
                      <Info size={14} className="text-gold mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-400 leading-relaxed">
                        <span className="text-slate-200 font-medium">Beginners can skip this.</span> These are the
                        exact numbers from your broker's MT5 symbol specification. Until they're verified here,
                        the calculator shows your risk amount but no lot size — on purpose.
                      </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <AdvField id="calc-symbol" label="Exact MT5 symbol (including suffix)" help="Type the symbol shown in your own MT5.">
                        <input id="calc-symbol" data-testid="calc-symbol" className={advInputCls} placeholder="e.g. XAUUSD or XAUUSDm" autoComplete="off" value={spec.symbol} onChange={(e) => setSpecField("symbol", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-currency" label="Account currency">
                        <select id="calc-currency" data-testid="calc-currency" className={advInputCls} value={spec.currency} onChange={(e) => setSpecField("currency", e.target.value)}>
                          <option value="USD">USD ($)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="OTHER">Other (enter below)</option>
                        </select>
                      </AdvField>
                      {spec.currency === "OTHER" && (
                        <AdvField id="calc-other-currency" label="Currency code">
                          <input id="calc-other-currency" data-testid="calc-other-currency" className={advInputCls} maxLength={8} placeholder="e.g. AUD" value={spec.otherCurrency} onChange={(e) => setSpecField("otherCurrency", e.target.value)} />
                        </AdvField>
                      )}
                      <AdvField id="calc-basis" label="Risk based on">
                        <select id="calc-basis" data-testid="calc-basis" className={advInputCls} value={spec.basis} onChange={(e) => setSpecField("basis", e.target.value)}>
                          <option value="Equity">Equity (recommended)</option>
                          <option value="Balance">Balance</option>
                        </select>
                      </AdvField>
                      <AdvField id="calc-method" label="Calculation input method">
                        <select id="calc-method" data-testid="calc-method" className={advInputCls} value={spec.method} onChange={(e) => setSpecField("method", e.target.value)}>
                          <option value="tick">Manual MT5 symbol specification (tick size + loss tick value)</option>
                          <option value="mt5quote">MT5 OrderCalcProfit quote (advanced / developer)</option>
                        </select>
                      </AdvField>
                      <AdvField id="calc-min-lot" label="Minimum volume (lots)">
                        <input id="calc-min-lot" data-testid="calc-min-lot" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="From MT5 specification" value={spec.minLot} onChange={(e) => setSpecField("minLot", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-volume-step" label="Volume step (lots)">
                        <input id="calc-volume-step" data-testid="calc-volume-step" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="From MT5 specification" value={spec.volumeStep} onChange={(e) => setSpecField("volumeStep", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-max-lot" label="Maximum volume (lots)">
                        <input id="calc-max-lot" data-testid="calc-max-lot" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="From MT5 specification" value={spec.maxLot} onChange={(e) => setSpecField("maxLot", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-tick-size" label="Tick size (price movement)">
                        <input id="calc-tick-size" data-testid="calc-tick-size" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="From MT5 specification" value={spec.tickSize} onChange={(e) => setSpecField("tickSize", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-tick-value" label="Losing-side tick value per 1.00 lot" help="In YOUR account currency. Use SYMBOL_TRADE_TICK_VALUE_LOSS if correctly provided for your account.">
                        <input id="calc-tick-value" data-testid="calc-tick-value" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="In YOUR account currency" value={spec.tickValueLoss} onChange={(e) => setSpecField("tickValueLoss", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-commission" label="Round-trip commission per 1 lot">
                        <input id="calc-commission" data-testid="calc-commission" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} value={spec.commissionPerLot} onChange={(e) => setSpecField("commissionPerLot", e.target.value)} />
                      </AdvField>
                      <AdvField id="calc-extra-ticks" label="Extra adverse-movement buffer (ticks)" help="Optional safety allowance; real slippage can exceed it.">
                        <input id="calc-extra-ticks" data-testid="calc-extra-ticks" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} value={spec.extraTicks} onChange={(e) => setSpecField("extraTicks", e.target.value)} />
                      </AdvField>
                    </div>

                    {spec.method === "mt5quote" && (
                      <div className="grid sm:grid-cols-2 gap-4" data-testid="quote-inputs">
                        <AdvField id="calc-probe-volume" label="MT5 quote volume (lots)" help="The volume passed to OrderCalcProfit.">
                          <input id="calc-probe-volume" data-testid="calc-probe-volume" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="e.g. 0.10" value={spec.probeVolume} onChange={(e) => setSpecField("probeVolume", e.target.value)} />
                        </AdvField>
                        <AdvField id="calc-quote-loss" label="Absolute quoted loss" help="Negative MT5 profit entered as a positive loss. Account currency.">
                          <input id="calc-quote-loss" data-testid="calc-quote-loss" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="Account currency" value={spec.quoteLoss} onChange={(e) => setSpecField("quoteLoss", e.target.value)} />
                        </AdvField>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowMargin(!showMargin)}
                      data-testid="margin-check-toggle"
                      className="flex items-center gap-2 text-sm text-gold hover:text-gold-bright transition-colors font-medium"
                    >
                      <ChevronDown size={14} className={`transition-transform duration-300 ${showMargin ? "rotate-180" : ""}`} />
                      Optional margin check (estimated only)
                    </button>
                    {showMargin && (
                      <div data-testid="margin-check-panel">
                        <p className="text-xs text-slate-500 leading-relaxed mb-4">
                          Margin is NOT the amount you lose at a stop. Enter a separately verified
                          estimated margin per 1 lot and available free margin to check whether the
                          risk-limited lot size may be affordable.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <AdvField id="calc-free-margin" label="Available free margin (account currency)">
                            <input id="calc-free-margin" data-testid="calc-free-margin" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="From MT5" value={spec.freeMargin} onChange={(e) => setSpecField("freeMargin", e.target.value)} />
                          </AdvField>
                          <AdvField id="calc-margin-per-lot" label="Estimated margin per 1 lot">
                            <input id="calc-margin-per-lot" data-testid="calc-margin-per-lot" type="number" min="0" step="any" inputMode="decimal" className={advInputCls} placeholder="MT5 OrderCalcMargin" value={spec.marginPerLot} onChange={(e) => setSpecField("marginPerLot", e.target.value)} />
                          </AdvField>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3 border-l-2 border-gold/60 rounded-sm bg-gold/5 px-4 py-3">
                      <ShieldAlert size={14} className="text-gold mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-300 leading-relaxed">
                        On MT5 desktop: <span className="text-slate-100 font-medium">Market Watch → right-click the exact symbol → Specification</span>.
                        Confirm tick size, losing-side tick value in your account currency, minimum/maximum
                        volume and volume step. If value conversion or commission is unclear, do not guess.
                      </p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer" data-testid="calc-verified-wrap">
                      <input
                        type="checkbox"
                        checked={verified}
                        onChange={(e) => setVerified(e.target.checked)}
                        data-testid="calc-verified-checkbox"
                        className="mt-0.5 w-4 h-4 shrink-0 accent-[#D4AF37]"
                      />
                      <span className="text-xs sm:text-sm text-slate-300 leading-relaxed" data-testid="calc-verify-text">
                        {demoMode
                          ? "I understand this is an ILLUSTRATIVE example only, NOT verified broker data. Do not use its volume for a real trade."
                          : "I have verified the specifications for this exact broker, MT5 symbol and account currency. I understand these are estimates, not guaranteed stop-loss outcomes."}
                      </span>
                    </label>

                    <button
                      onClick={loadIllustration}
                      data-testid="calc-demo-button"
                      className="inline-flex items-center gap-2 border border-line text-slate-400 font-medium text-xs px-4 py-2.5 rounded-sm hover:border-gold/50 hover:text-gold transition-colors duration-300"
                    >
                      <Sparkles size={13} />
                      Load example specifications (demo only)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          {/* ---------- Guide tiles ---------- */}
          <div className="mt-8 max-w-3xl mx-auto grid sm:grid-cols-3 gap-5">
            {[
              ["What's a lot?", "A lot is just trade size. Bigger lot = bigger wins AND bigger losses. This tool keeps the size matched to your risk."],
              ["What's a stop-loss?", "The price where you exit a losing trade on purpose. It's how you decide your risk before you enter, not after."],
              ["Why verify broker specs?", "Gold at one broker can be worth a different amount per point than at another. Verified numbers mean your risk plan is real."],
            ].map(([title, body], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="h-full border border-line rounded-md bg-card2/40 p-6" data-testid={`calc-guide-${i + 1}`}>
                  <p className="font-display font-bold text-base text-slate-100">{title}</p>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 pt-6 border-t border-line/60 text-[11px] text-slate-500 leading-relaxed max-w-3xl mx-auto" data-testid="calc-disclaimer">
            ATLAS Academy · Educational risk estimation only. No position size is safe from market
            gaps, execution delays, changing broker conditions or unusual market movements.
            Independently confirm results against your trading platform. Never enter MT5 account
            passwords into this calculator.{" "}
            <Link to="/" state={{ scrollTo: "education" }} className="text-gold hover:text-gold-bright transition-colors" data-testid="calc-back-link">
              Back to the Academy
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PositionSizing;
